/**
 * El talón en pantalla, antes de mandarlo a la impresora.
 *
 * Se ve primero y se imprime después, en vez de disparar `window.print()`
 * directo: el papel que se le da al cliente es el único documento del taller y
 * conviene mirarlo una vez. Además así se puede leer desde el celular sin
 * imprimir nada.
 *
 * `lg` porque adentro va una hoja: es una vista previa imprimible, el unico
 * contenido que justifica el ancho mayor. La hoja en si (`TalonReparacion`) es
 * blanco y negro a proposito y no se toca.
 *
 * La copia que va a la impresora se cuelga aparte, directo del `body`: la de la
 * vista previa queda recortada por la caja del diálogo y la hoja salía en
 * blanco. Ver `globals.css`, "Impresión".
 *
 * Al lado de Imprimir, mandarlo por WhatsApp al número del cliente: el mensaje
 * lleva el link a la página del talón (`/t/:token`, en el backend), que el
 * cliente abre y guarda como PDF. Para cuando no hay papel a mano.
 */
import { createPortal } from "react-dom";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import type { EmpresaConfig } from "@/lib/tipos";

import { BotonWhatsapp } from "./BotonWhatsapp";
import { nombreOrden, type OrdenTaller } from "./logica";
import { mensajeTalon } from "./mensajes";
import { TalonReparacion } from "./TalonReparacion";

const BASE_PUBLICA = import.meta.env.VITE_URL_PUBLICA ?? window.location.origin;

export interface DialogTalonProps {
  orden: OrdenTaller | null;
  empresa?: EmpresaConfig | null;
  telefono?: string | null;
  onCerrar: () => void;
}

export function DialogTalon({ orden, empresa, telefono, onCerrar }: DialogTalonProps): JSX.Element {
  return (
    <Dialog open={orden !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="lg">
        {orden ? (
          <>
            <DialogHeader className="no-imprimir">
              <DialogTitle>Talón de {nombreOrden(orden)}</DialogTitle>
              <DialogDescription>
                Es lo que se lleva el cliente. Las condiciones van impresas al pie: recibir el talón
                es aceptarlas.
              </DialogDescription>
            </DialogHeader>

            <DialogBody>
              <div className="rounded-md border border-border">
                <TalonReparacion orden={orden} empresa={empresa} telefono={telefono} />
              </div>
            </DialogBody>

            <DialogFooter className="no-imprimir">
              <Button type="button" variant="ghost" onClick={onCerrar}>
                Cerrar
              </Button>
              {orden.token_publico ? (
                <BotonWhatsapp
                  telefono={telefono}
                  texto={mensajeTalon(
                    orden,
                    `${BASE_PUBLICA.replace(/\/+$/, "")}/t/${orden.token_publico}`,
                  )}
                  etiqueta="Mandar por WhatsApp"
                />
              ) : null}
              <Button type="button" onClick={() => window.print()}>
                Imprimir
              </Button>
            </DialogFooter>

            {createPortal(
              <div className="solo-imprimir">
                <TalonReparacion orden={orden} empresa={empresa} telefono={telefono} paraImprimir />
              </div>,
              document.body,
            )}
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
